import { AlertTriangle, Bell, CheckCircle } from "lucide-react";
import { MATERIAL_CATEGORIES } from "..";

const StockAlertsSection = ({ ctx }) => {
  const {
    inwardForm,
    openDialog,
    setInwardForm,
    stockSummary
  } = ctx;
  return (
    <>
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading font-bold text-xl text-slate-800">
              <Bell className="w-5 h-5 inline mr-2 text-error" />
              Low Stock Alerts
            </h3>
            <div className="text-sm text-slate-600">
              Items below minimum stock level
            </div>
          </div>

          {/* Category-wise Alert Summary */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {MATERIAL_CATEGORIES.map((cat) => {
              const categoryItems = stockSummary.filter(s => s.category === cat.id || (!s.category && cat.id === "construction"));
              const lowStockItems = categoryItems.filter(s => s.low_stock_alert);
              const criticalItems = categoryItems.filter(s => s.current_stock === 0 && s.minimum_stock > 0);
              return (
                <div key={cat.id} className={`stat-card border-l-4 ${lowStockItems.length > 0 ? 'border-l-error bg-error/5' : 'border-l-success bg-success/5'}`}>
                  <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">{cat.label}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className={`font-heading font-bold text-2xl ${lowStockItems.length > 0 ? 'text-error' : 'text-success'}`}>
                        {lowStockItems.length}
                      </p>
                      <p className="text-xs text-slate-500">Low Stock</p>
                    </div>
                    {criticalItems.length > 0 && (
                      <div className="text-right">
                        <p className="font-heading font-bold text-2xl text-error animate-pulse">{criticalItems.length}</p>
                        <p className="text-xs text-error">Out of Stock</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Alerts List */}
          <div className="stat-card">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Material</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Category</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Current Stock</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Min Stock</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Shortage</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Severity</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {stockSummary
                  .filter(s => s.low_stock_alert)
                  .sort((a, b) => (a.current_stock / (a.minimum_stock || 1)) - (b.current_stock / (b.minimum_stock || 1)))
                  .map((item) => {
                    const categoryInfo = MATERIAL_CATEGORIES.find(c => c.id === item.category) || MATERIAL_CATEGORIES[0];
                    const shortage = (item.minimum_stock || 0) - (item.current_stock || 0);
                    const severity = item.current_stock === 0 ? "critical" : item.current_stock < (item.minimum_stock * 0.5) ? "high" : "medium";
                    return (
                      <tr key={item.id} className={`border-b border-slate-200 ${severity === "critical" ? 'bg-error/10' : severity === "high" ? 'bg-warning/10' : 'bg-amber-50'}`}>
                        <td className="py-3 px-4 font-medium text-primary">
                          <AlertTriangle className={`w-4 h-4 inline mr-2 ${severity === "critical" ? 'text-error animate-pulse' : 'text-warning'}`} />
                          {item.name}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full bg-${categoryInfo.color}/10 text-${categoryInfo.color}`}>
                            {categoryInfo.label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm font-bold text-error">
                          {item.current_stock?.toFixed(2)} {item.unit}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {item.minimum_stock?.toFixed(2)} {item.unit}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm font-bold text-error">
                          -{shortage.toFixed(2)} {item.unit}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full font-bold ${
                            severity === "critical" ? 'bg-error text-white' :
                            severity === "high" ? 'bg-warning text-white' :
                            'bg-amber-200 text-amber-800'
                          }`}>
                            {severity === "critical" ? "OUT OF STOCK" : severity === "high" ? "CRITICAL" : "LOW"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => { openDialog("inward"); setInwardForm({...inwardForm, material_id: item.id, category: item.category || "construction"}); }}
                            className="btn-primary text-xs px-3 py-1"
                          >
                            + Inward
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {stockSummary.filter(s => s.low_stock_alert).length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-success" />
                <h3 className="font-heading font-bold text-xl mb-2 text-success">All Stock Levels Normal</h3>
                <p className="text-slate-600">No materials are below minimum stock level.</p>
              </div>
            )}
          </div>
        </div>
    </>
  );
};

export default StockAlertsSection;
